/* eslint-disable testing-library/no-debugging-utils */
import { type ApiObject, type IdentifyTraits, RudderAnalytics } from '@rudderstack/analytics-js'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { Logger } from '@egw/logger'
import { isValidConfig } from '@egw/config'
import { type Config, configSchema } from '../../config/config'
import { createBrazeCustomIntegration } from '../../integrations/braze-custom-integration'
import { createFullStoryCustomIntegration } from '../..'

const log = new Logger('Analytics Service')

let instance: AnalyticsService | null = null

interface EventPayload {
  eventName: string
  props?: ApiObject | null
  deviceContext?: Record<string, unknown>
  identityContext?: Record<string, unknown>
  context?: Record<string, unknown> // Legacy
}

class AnalyticsService {
  private client: RudderAnalytics | undefined
  private userId: string | undefined
  private deviceContext: Record<string, unknown> = {}
  private identityContext: Record<string, unknown> = {}
  private jobQueue: (() => void)[] = []

  constructor() {
    if (!instance) {
      instance = this

      // ===== EVENT LISTENER =====
      const eventListener = (data: unknown): void => {
        const event = data as { detail: EventPayload }
        const eventData = event.detail

        this.enqueue(() => {
          log.debug('Analytics event:', eventData.eventName, {
            props: eventData.props,
            deviceContext: eventData.deviceContext,
            identityContext: eventData.identityContext,
          })

          if (this.client && eventData.eventName === 'pageView') {
            // Page views include device context + props
            // Identity context is automatically appended by RudderStack after identify() is called
            const pageProperties = {
              ...eventData.props,
              ...eventData.deviceContext,
            } as ApiObject
            log.debug('Page event properties (with device context):', pageProperties)
            this.client.page(eventData.eventName, pageProperties)
          } else if (this.client && eventData.eventName && eventData.eventName !== 'pageView') {
            // Track events include device context + props
            // Identity context is automatically appended by RudderStack after identify() is called
            const trackProperties = {
              ...eventData.props,
              ...eventData.deviceContext,
            } as ApiObject
            log.debug('Track event properties (with device context):', trackProperties)
            this.client.track(eventData.eventName, trackProperties)
          }
        })
      }
      notifications.on(NOTIFICATIONS.EVENTS.EVENT, eventListener)

      // ===== DEVICE CONTEXT LISTENER =====
      const deviceContextListener = (data: unknown): void => {
        const context = data as { detail: Record<string, unknown> }
        log.debug('Device Context Listener Invoked', {
          newDeviceContext: context.detail,
        })

        // Update device context (persists across sessions)
        this.deviceContext = {
          ...this.deviceContext,
          ...context.detail,
        }

        // Don't call identify for device context changes
        // Device context is sent with each event instead
        log.debug('Device context updated (not triggering identify)', this.deviceContext)

        this.enqueue(() => {
          if (this.client) {
            log.debug('Firing Device Context notification Update', { deviceContext: this.deviceContext })
            this.client.track('egw_device_context_update', { deviceContext: this.deviceContext } as ApiObject)
          }
        })
      }
      notifications.on(NOTIFICATIONS.EVENTS.SET_DEVICE_CONTEXT, deviceContextListener)

      // ===== IDENTITY CONTEXT LISTENER =====
      const identityContextListener = (data: unknown): void => {
        const context = data as { detail: Record<string, unknown> }
        log.debug('Identity Context Listener Invoked', {
          newIdentityContext: context.detail,
        })

        // Check if userId is present in the context
        if (context.detail.userId && !this.userId) {
          this.userId = context.detail.userId as string
        }

        // Update identity context
        this.identityContext = {
          ...this.identityContext,
          ...context.detail,
        }

        this.enqueue(() => {
          if (this.client) {
            const userId = this.userId ?? '' // Anonymous until logged in
            log.debug('Identify enqueued with identity context:', this.identityContext)

            // Identify with ONLY identity context (not device context)
            this.client.identify(userId, this.identityContext as IdentifyTraits)
          }
        })
      }
      notifications.on(NOTIFICATIONS.EVENTS.SET_IDENTITY_CONTEXT, identityContextListener)

      // ===== CLEAR IDENTITY CONTEXT LISTENER =====
      const clearIdentityContextListener = (): void => {
        log.debug('Clear Identity Context Listener Invoked')

        // Clear identity context
        this.identityContext = {}
        this.userId = undefined

        this.enqueue(() => {
          if (this.client) {
            log.debug('Resetting to anonymous user after identity cleared')
            // Reset to anonymous user
            this.client.reset()
          }
        })
      }
      notifications.on(NOTIFICATIONS.EVENTS.CLEAR_IDENTITY_CONTEXT, clearIdentityContextListener)

      // ===== LEGACY CONTEXT LISTENER (Backward Compatibility) =====
      const legacyContextListener = (data: unknown): void => {
        const context = data as { detail: Record<string, unknown> }
        log.warn('Legacy SET_CONTEXT listener invoked - this is deprecated', {
          context: context.detail,
        })

        // For backward compatibility, treat as identity context if userId present
        if (context.detail.userId) {
          identityContextListener(data)
        }
      }
      notifications.on(NOTIFICATIONS.EVENTS.SET_CONTEXT, legacyContextListener)
    }
  }

  private enqueue(action: () => void): void {
    if (this.client && typeof window !== 'undefined') {
      action()
    } else {
      log.debug('Queuing job as service is not initialized')
      this.jobQueue.push(action)
    }
  }

  private flushQueue(): void {
    while (this.jobQueue.length > 0) {
      const action = this.jobQueue.shift()
      action?.()
    }
  }

  init = (config: Config): void => {
    if (!isValidConfig(config, configSchema)) {
      log.error('Analytics service configuration is invalid')
      throw new Error('Analytics service configuration is invalid')
    }
    if (!this.client) {
      if (typeof window === 'undefined') return
      try {
        // Initialize Google Tag Manager
        if (config.gtm) {
          const script = document.createElement('script')
          script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src="${config.gtm.baseUrl}/${config.gtm.scriptPath}?"+i;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${config.gtm.scriptParams}');`
          const noscript = document.createElement('noscript')
          const iframe = document.createElement('iframe')
          iframe.src = `${config.gtm.baseUrl}/ns.html?id=${config.gtm.id}`
          iframe.height = '0'
          iframe.width = '0'
          iframe.style.display = 'none'
          iframe.style.visibility = 'hidden'
          noscript.appendChild(iframe)
          document.head.prepend(noscript)
          document.head.prepend(script)
          log.info('Google Tag Manager is initialized')
        }

        // Init RudderStack
        const rudderAnalytics = new RudderAnalytics()

        // Register Braze Custom Integration before loading RudderStack
        // This ensures our custom integration handles Braze initialization
        // instead of RudderStack's native Braze destination
        const brazeCustomIntegration = createBrazeCustomIntegration({
          apiKey: config.braze.apiKey,
          apiEndpoint: config.braze.apiEndpoint,
          enableLogging: config.braze.enableLogging,
        })

        log.debug('Registering Braze custom integration with RudderStack', { brazeCustomIntegration })

        // Register FullStory custom integration before loading RudderStack
        const fullStoryCustomIntegration = createFullStoryCustomIntegration({
          orgId: config.fullStory.orgId,
          enableLogging: config.fullStory.devMode,
        })

        // Check if addCustomIntegration is available at runtime
        if (typeof rudderAnalytics.addCustomIntegration === 'function') {
          rudderAnalytics.addCustomIntegration(config.rudderstack.brazeDestinationId, brazeCustomIntegration)
          log.info('Braze custom integration registered successfully')

          rudderAnalytics.addCustomIntegration(config.rudderstack.fullStoryDestinationId, fullStoryCustomIntegration)
          log.info('FullStory custom integration registered successfully')
        } else {
          log.warn(
            'addCustomIntegration() not available in this RudderStack version, Braze custom integration will not be registered',
          )

          log.warn(
            'addCustomIntegration() not available in this RudderStack version, FullStory custom integration will not be registered',
          )
        }

        rudderAnalytics.load(config.rudderstack.writeKey, config.rudderstack.dataPlaneUrl, {
          configUrl: config.rudderstack.configUrl,
          destSDKBaseURL: `${config.rudderstack.destSDKBaseURL}/v3/modern/js-integrations`,
          pluginsSDKBaseURL: `${config.rudderstack.destSDKBaseURL}/v3/modern/plugins`,
        })
        this.client = rudderAnalytics
        log.info('RudderStack is initialized')
        this.enqueue(() => {
          log.debug('Firing Analytics Init notification')
          notifications.dispatch(NOTIFICATIONS.ANALYTICS.INIT)
        })
        this.flushQueue()
      } catch (error) {
        log.error('Failed to initialize RudderStack', error)
      }
    } else {
      log.warn('RudderStack has already been initialized')
    }
  }
}

export const analyticsService = new AnalyticsService()
